import { Link, Routes, Route } from 'react-router-dom'
import { Suspense, lazy, useEffect } from 'react'
import TopBar from '@/components/TopBar'
import Footer from '@/components/Footer'
import AnimatedPage from '@/components/AnimatedPage'
import ResearchShell from '@/components/ResearchShell'

const LandingV2Page = lazy(() => import('@/pages/LandingV2Page'))
const InvitePage = lazy(() => import('@/pages/InvitePage'))
const InviteLandingV2PreviewPage = lazy(() => import('@/pages/InviteLandingV2PreviewPage'))
const AuthCallbackPage = lazy(() => import('@/pages/AuthCallbackPage'))
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const CompleteProfilePage = lazy(() => import('@/pages/CompleteProfilePage'))
const StyleguidePage = lazy(() => import('@/pages/StyleguidePage'))
const LegalPage = lazy(() => import('@/pages/LegalPage'))
const AboutPage = lazy(() => import('@/pages/AboutPage'))
const WelcomePage = lazy(() => import('@/pages/WelcomePage'))
const OfferV2Page = lazy(() => import('@/pages/OfferV2Page'))
const OfferV3IndexPage = lazy(() => import('@/pages/offer-v3/OfferV3IndexPage'))
const CinematicTrialPage = lazy(() => import('@/pages/offer-v3/CinematicTrialPage'))
const ControlEditTrialPage = lazy(() => import('@/pages/offer-v3/ControlEditTrialPage'))
const QuietProofTrialPage = lazy(() => import('@/pages/offer-v3/QuietProofTrialPage'))
const OfferV4IndexPage = lazy(() => import('@/pages/offer-v4/OfferV4IndexPage'))
const PeaceOfMindPage = lazy(() => import('@/pages/offer-v4/PeaceOfMindPage'))
const BlindSpotPage = lazy(() => import('@/pages/offer-v4/BlindSpotPage'))
const PortableProofPage = lazy(() => import('@/pages/offer-v4/PortableProofPage'))
const OfferV5IndexPage = lazy(() => import('@/pages/offer-v5/OfferV5IndexPage'))
const OfferV5PeaceOfMindPage = lazy(() => import('@/pages/offer-v5/PeaceOfMindPage'))
const OfferV5BlindSpotPage = lazy(() => import('@/pages/offer-v5/BlindSpotPage'))
const OfferV5PortableProofPage = lazy(() => import('@/pages/offer-v5/PortableProofPage'))
const OfferV6Page = lazy(() => import('@/pages/offer-v6/OfferV6Page'))
const CurrentSiteRefreshPage = lazy(() => import('@/pages/CurrentSiteRefreshPage'))
const ResearchIndexPage = lazy(() => import('@/pages/ResearchIndexPage'))
const ResearchArticlePage = lazy(() => import('@/pages/ResearchArticlePage'))
const FoundingLandingPage = lazy(() => import('@/founding/FoundingLandingPage'))
const FoundingReferralPage = lazy(() => import('@/founding/FoundingReferralPage'))
const FoundingEndedPage = lazy(() => import('@/founding/FoundingEndedPage'))
const FoundingConceptLab = lazy(() => import('@/founding/lab/FoundingConceptLab'))

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <AnimatedPage>
      <div className="flex min-h-screen flex-col">
        <TopBar />
        <main className="flex-1">{children}</main>
        <Footer />
      </div>
    </AnimatedPage>
  )
}

function NotFoundPage() {
  useEffect(() => {
    const previousTitle = document.title
    const existingRobots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]')
    const previousRobots = existingRobots?.content
    const robots = existingRobots ?? document.createElement('meta')
    if (!existingRobots) {
      robots.name = 'robots'
      document.head.appendChild(robots)
    }
    robots.content = 'noindex, nofollow'
    document.title = 'Page not found | Aelo'

    return () => {
      document.title = previousTitle
      if (existingRobots && previousRobots !== undefined) existingRobots.content = previousRobots
      else robots.remove()
    }
  }, [])

  return (
    <section className="bg-aelo-snow">
      <div className="aelo-container flex min-h-[72vh] flex-col items-start justify-center py-20">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-aelo-water-deep">
          Page not found
        </p>
        <h1 className="mt-5 max-w-3xl text-[clamp(3rem,8vw,6rem)] font-semibold leading-[0.88] tracking-[-0.055em]">
          This page does not exist.
        </h1>
        <Link
          to="/"
          className="mt-8 inline-flex min-h-12 items-center justify-center rounded-full bg-aelo-black px-7 text-sm font-semibold text-white transition-colors hover:bg-aelo-slate focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-aelo-water"
        >
          Return to Aelo
        </Link>
      </div>
    </section>
  )
}

export default function App() {
  return (
    <Suspense
      fallback={
        <div
          role="status"
          aria-live="polite"
          className="flex min-h-screen items-center justify-center"
        >
          <div
            aria-hidden="true"
            className="h-8 w-8 animate-spin rounded-full border-2 border-aelo-mist border-t-aelo-water"
          />
          <span className="sr-only">Loading page</span>
        </div>
      }
    >
      <Routes>
        <Route
          path="/"
          element={
            <PageShell>
              <CurrentSiteRefreshPage />
            </PageShell>
          }
        />
        <Route
          path="/landing-v2"
          element={
            <PageShell>
              <LandingV2Page />
            </PageShell>
          }
        />
        <Route path="/founding" element={<FoundingLandingPage />} />
        <Route path="/founding/lab" element={<FoundingConceptLab />} />
        <Route
          path="/founding/editorial"
          element={<FoundingLandingPage visualVariant="editorial" />}
        />
        <Route
          path="/founding/interactive"
          element={<FoundingLandingPage visualVariant="interactive" />}
        />
        <Route
          path="/founding/kit"
          element={<FoundingLandingPage visualVariant="kit" />}
        />
        <Route path="/founding/refer" element={<FoundingReferralPage />} />
        <Route path="/founding/ended" element={<FoundingEndedPage />} />
        <Route
          path="/website-test"
          element={
            <PageShell>
              <CurrentSiteRefreshPage />
            </PageShell>
          }
        />
        <Route
          path="/offer-v2"
          element={
            <AnimatedPage>
              <OfferV2Page />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v2/:variant"
          element={
            <AnimatedPage>
              <OfferV2Page />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v3"
          element={
            <AnimatedPage>
              <OfferV3IndexPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v3/cinematic"
          element={
            <AnimatedPage>
              <CinematicTrialPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v3/control"
          element={
            <AnimatedPage>
              <ControlEditTrialPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v3/quiet-proof"
          element={
            <AnimatedPage>
              <QuietProofTrialPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v4"
          element={
            <AnimatedPage>
              <OfferV4IndexPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v4/peace-of-mind"
          element={
            <AnimatedPage>
              <PeaceOfMindPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v4/blind-spot"
          element={
            <AnimatedPage>
              <BlindSpotPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v4/portable-proof"
          element={
            <AnimatedPage>
              <PortableProofPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v5"
          element={
            <AnimatedPage>
              <OfferV5IndexPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v5/peace-of-mind"
          element={
            <AnimatedPage>
              <OfferV5PeaceOfMindPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v5/blind-spot"
          element={
            <AnimatedPage>
              <OfferV5BlindSpotPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v5/portable-proof"
          element={
            <AnimatedPage>
              <OfferV5PortableProofPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/offer-v6"
          element={
            <AnimatedPage>
              <OfferV6Page />
            </AnimatedPage>
          }
        />
        <Route
          path="/invite"
          element={
            <PageShell>
              <InvitePage />
            </PageShell>
          }
        />
        <Route
          path="/invite/:code"
          element={
            <PageShell>
              <InvitePage />
            </PageShell>
          }
        />
        <Route
          path="/invite-v2-preview"
          element={
            <PageShell>
              <InviteLandingV2PreviewPage />
            </PageShell>
          }
        />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/dashboard"
          element={
            <PageShell>
              <DashboardPage />
            </PageShell>
          }
        />
        <Route
          path="/complete-profile"
          element={
            <PageShell>
              <CompleteProfilePage />
            </PageShell>
          }
        />
        <Route
          path="/styleguide"
          element={
            <PageShell>
              <StyleguidePage />
            </PageShell>
          }
        />
        <Route
          path="/about"
          element={
            <PageShell>
              <AboutPage />
            </PageShell>
          }
        />
        <Route
          path="/research"
          element={
            <ResearchShell>
              <ResearchIndexPage />
            </ResearchShell>
          }
        />
        <Route
          path="/research/:slug"
          element={
            <ResearchShell>
              <ResearchArticlePage />
            </ResearchShell>
          }
        />
        <Route
          path="/welcome"
          element={
            <AnimatedPage>
              <WelcomePage />
            </AnimatedPage>
          }
        />
        <Route
          path="/privacy"
          element={
            <PageShell>
              <LegalPage type="privacy" />
            </PageShell>
          }
        />
        <Route
          path="/terms"
          element={
            <PageShell>
              <LegalPage type="terms" />
            </PageShell>
          }
        />
        <Route
          path="*"
          element={
            <PageShell>
              <NotFoundPage />
            </PageShell>
          }
        />
      </Routes>
    </Suspense>
  )
}
